import { useState, useEffect, useCallback } from 'react';
import { subscribeToMessages, sendMessage } from '../firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage messages in a raffle thread
 *
 * @param {string} raffleId - The raffle ID for the message thread
 * @param {string} otherUserId - The other participant's user ID
 * @returns {object} - Messages state and actions
 */
export function useMessages(raffleId, otherUserId) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Subscribe to messages
  useEffect(() => {
    if (!raffleId || !user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToMessages(
      raffleId,
      user.uid,
      (newMessages) => {
        setMessages(newMessages);
        setLoading(false);
      },
      (err) => {
        console.error('Messages subscription error:', err);
        setError('Unable to load messages');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [raffleId, user?.uid]);

  // Send a message
  const send = useCallback(
    async (text) => {
      if (!user?.uid || !otherUserId || !raffleId || !text.trim()) {
        return false;
      }

      setSending(true);
      setError(null);

      try {
        await sendMessage(raffleId, user.uid, otherUserId, text);
        return true;
      } catch (err) {
        // ALREADY_EXISTS means message was created but SDK retried - ignore
        if (err.code === 'already-exists' || err.message?.includes('ALREADY_EXISTS')) {
          return true;
        }
        setError('Failed to send message. Please try again.');
        return false;
      } finally {
        setSending(false);
      }
    },
    [raffleId, user?.uid, otherUserId]
  );

  // Count unread messages for the current user
  const unreadCount = messages.filter(
    (m) => m.receiverId === user?.uid && !m.readAt
  ).length;

  return {
    messages,
    loading,
    sending,
    error,
    send,
    unreadCount,
  };
}

export default useMessages;
